import clsx from 'clsx';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@/ui/Alert';
import { Button } from '@/ui/Button';

import { formatDraftUpdatedAt } from '../utils/formatDraftUpdatedAt';

type SourceKey = 'autosave' | 'baseline' | 'empty';

type AutosaveSourceBannerProps = {
	activeSource: SourceKey;
	canSwitchSource: boolean;
	className?: string;
	draftUpdatedAt?: string | null;
	hasAutosaveSource: boolean;
	hasBaselineSource: boolean;
	hasEmptySource?: boolean;
	onSwitchSource: (source: SourceKey) => void;
};

const getSourceLabel = (source: SourceKey, t: (key: string, options?: Record<string, unknown>) => string): string => {
	switch (source) {
		case 'autosave':
			return t('autosave.banner.sourceDraft');
		case 'empty':
			return t('autosave.banner.sourceEmpty');
		case 'baseline':
		default:
			return t('autosave.banner.sourceBaseline');
	}
};

const AutosaveSourceBanner: React.FC<AutosaveSourceBannerProps> = ({
	activeSource,
	canSwitchSource,
	className,
	draftUpdatedAt,
	hasAutosaveSource,
	hasBaselineSource,
	onSwitchSource,
}) => {
	const { t } = useTranslation();
	const formattedDraftUpdatedAt = formatDraftUpdatedAt(draftUpdatedAt);

	if (!canSwitchSource) {
		return null;
	}

	const changeSourceButton =
		activeSource === 'autosave'
			? hasBaselineSource
				? {
						label: t('autosave.banner.toBaseline'),
						source: 'baseline' as const,
				  }
				: null
			: hasAutosaveSource
				? {
						label: t('autosave.banner.toDraft'),
						source: 'autosave' as const,
				  }
				: hasBaselineSource && activeSource === 'empty'
					? {
							label: t('autosave.banner.toBaseline'),
							source: 'baseline' as const,
					  }
				: null;

	return (
		<Alert className={clsx('mb-4 overflow-hidden px-4 py-3', className)}>
			<AlertDescription className="mt-0 flex flex-col gap-3">
				<div className="grid grid-cols-1 gap-3 min-[690px]:grid-cols-[minmax(0,1fr)_auto] min-[690px]:items-start min-[690px]:gap-x-4">
					<div className="flex min-w-0 flex-col justify-center gap-1.5">
						<AlertTitle className="mb-0">
							{t('autosave.banner.title')}
						</AlertTitle>

						<span className="text-left break-words">
							{t('autosave.banner.active', { source: getSourceLabel(activeSource, t) })}
						</span>

						{activeSource === 'autosave' && formattedDraftUpdatedAt && (
							<span className="text-left break-words text-sm text-brand-gray-2">
								{t('autosave.banner.updatedAt', { date: formattedDraftUpdatedAt })}
							</span>
						)}
					</div>

					{changeSourceButton && (
						<div className="flex w-full min-[690px]:w-auto min-[690px]:items-start min-[690px]:justify-end">
							<Button
								size="sm"
								variant="secondary"
								className="w-full whitespace-normal px-3 py-2 text-center min-[690px]:h-[52px] min-[690px]:w-[240px] min-[690px]:min-w-[240px]"
								onClick={() => onSwitchSource(changeSourceButton.source)}
							>
								{changeSourceButton.label}
							</Button>
						</div>
					)}
				</div>
				<p className="m-0 break-words text-left text-xs leading-4 text-brand-gray-2">
					{t('autosave.banner.note')}
				</p>
			</AlertDescription>
		</Alert>
	);
};

export { AutosaveSourceBanner };
